import React, { createContext, useContext, useState } from "react";
import "@ui5/webcomponents/dist/Panel.js";

// Create context for sidebar state
const SidebarContext = createContext();

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within SidebarProvider");
  }
  return context;
};

export const Sidebar = ({
  children,
  className = "",
  isCollapsed = false,
  ...props
}) => {
  return (
    <aside
      className={`h-full transition-all duration-300 ${
        isCollapsed ? "w-[80px] min-w-[80px] max-w-[80px]" : ""
      } ${className}`}
      {...props}
    >
      {children}
    </aside>
  );
};

export const SidebarProvider = ({ children, className = "", ...props }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleSidebar }}>
      <div className={`flex ${className}`} {...props}>
        {children}
      </div>
    </SidebarContext.Provider>
  );
};

export const SidebarContent = ({ children, className = "", ...props }) => {
  return (
    <div className={`flex flex-col ${className}`} {...props}>
      {children}
    </div>
  );
};

export const SidebarGroup = ({ children, className = "", ...props }) => {
  return (
    <div className={`space-y-2 ${className}`} {...props}>
      {children}
    </div>
  );
};

export const SidebarGroupLabel = ({ children, className = "", ...props }) => {
  return (
    <div
      className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

export const SidebarMenu = ({ children, className = "", ...props }) => {
  return (
    <nav className={`space-y-1 ${className}`} {...props}>
      {children}
    </nav>
  );
};

export const SidebarMenuItem = ({ children, className = "", ...props }) => {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
};

export const SidebarMenuButton = ({
  children,
  className = "",
  isActive = false,
  ...props
}) => {
  const baseClasses =
    "flex items-center w-full px-4 py-2 text-sm font-medium rounded-md transition-colors";
  const activeClasses = isActive
    ? "bg-blue-100 text-blue-900"
    : "text-gray-700 hover:bg-gray-100 hover:text-gray-900";

  return (
    <button
      className={`${baseClasses} ${activeClasses} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const SidebarTrigger = ({
  children,
  className = "",
  isCollapsed = false,
  ...props
}) => {
  return (
    <button className={`p-2 ${className}`} {...props}>
      {children}
    </button>
  );
};

export const SidebarHeader = ({ children, className = "", ...props }) => {
  return (
    <div className={`p-4 border-b ${className}`} {...props}>
      {children}
    </div>
  );
};

export const SidebarFooter = ({ children, className = "", ...props }) => {
  return (
    <div className={`p-4 border-t mt-auto ${className}`} {...props}>
      {children}
    </div>
  );
};

export { Sidebar as default };
